import { useMemo, useState } from 'react';
import type { FleetClient, FleetGroupClient, BulkFleetResult, GalaxyMapResponse } from '@galaxis/shared';
import { bulkFleetCommand, fetchFleets } from '../../api/galaxy.ts';
import { useGameStore } from '../../store/game-store.ts';
import { ConfirmDialog } from './ConfirmDialog.tsx';
import { GroupDialog } from './FleetGroups.tsx';
import { newIdempotencyKey } from './fleet-format.ts';
import { bulkSummary } from './fleet-overview.ts';

interface Props {
  selected: FleetClient[];
  groups: FleetGroupClient[];
  onGroups: (g: FleetGroupClient[]) => void;
  map: GalaxyMapResponse | null;
  onClear: () => void;
  onAll: () => void;
  allSelected: boolean;
}

type Dialog = { kind: 'move' } | { kind: 'hold' } | { kind: 'groups' } | { kind: 'results'; text: string; rows: Array<BulkFleetResult & { name: string }> } | null;

const BTN = 'rounded-lg text-sm font-medium px-3 border disabled:opacity-40';

/** Sticky action bar for the multi-selection: one command for many fleets, with a per-fleet result. */
export function FleetBulkBar({ selected, groups, onGroups, map, onClear, onAll, allSelected }: Props) {
  const setFleets = useGameStore(s => s.setFleets);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState('');
  const [query, setQuery] = useState('');
  const [hours, setHours] = useState(1);
  const [replace, setReplace] = useState(true);
  // One key per dialog opening: a double click / retry re-sends the same batch, never a second one
  const [batchKey, setBatchKey] = useState(() => newIdempotencyKey('bulk').slice(0, 80));

  const ids = selected.map(f => f.id);
  const systems = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('hu');
    return (map?.systems ?? []).filter(s => !q || s.name.toLocaleLowerCase('hu').includes(q)).sort((a, b) => a.name.localeCompare(b.name, 'hu')).slice(0, 8);
  }, [map, query]);

  function open(d: Dialog) { setError(null); setBatchKey(newIdempotencyKey('bulk').slice(0, 80)); setDialog(d); }

  async function send(command: Parameters<typeof bulkFleetCommand>[0]['command'], doReplace: boolean) {
    setBusy(true); setError(null);
    try {
      const res = await bulkFleetCommand({ fleetIds: ids, command, idempotencyKey: batchKey, replace: doReplace });
      try { setFleets((await fetchFleets()).fleets); } catch { /* the live update catches up */ }
      const rows = res.results.map(r => ({ ...r, name: selected.find(f => f.id === r.fleetId)?.name ?? '—' }));
      setDialog({ kind: 'results', text: bulkSummary(res.results), rows });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ismeretlen hiba');
    } finally {
      setBusy(false);
    }
  }

  const mapName = (id: string) => map?.systems.find(s => s.id === id)?.name ?? '—';

  return (
    <>
      <div className="sticky bottom-0 z-20 mt-3 px-3 py-2 border-t border-cyan-500/30 bg-slate-950/95 backdrop-blur space-y-2" data-testid="bulk-bar">
        <div className="flex items-center justify-between gap-2 text-xs text-slate-300">
          <span data-testid="bulk-count">{selected.length} flotta kijelölve</span>
          <span className="flex gap-2">
            <button type="button" onClick={onAll} className="text-cyan-400 px-2" style={{ minHeight: 44 }}>{allSelected ? 'Egyik sem' : 'Mind (látható)'}</button>
            <button type="button" onClick={onClear} className="text-slate-400 px-2" style={{ minHeight: 44 }} data-testid="bulk-clear">Kilépés</button>
          </span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <button type="button" disabled={selected.length === 0} onClick={() => open({ kind: 'move' })} data-testid="bulk-move" className={BTN}
            style={{ minHeight: 44, color: '#00e5ff', borderColor: 'rgba(0,229,255,0.5)' }}>🚀 Mozgat</button>
          <button type="button" disabled={selected.length === 0} onClick={() => open({ kind: 'hold' })} data-testid="bulk-hold" className={BTN}
            style={{ minHeight: 44, color: '#fbbf24', borderColor: 'rgba(251,191,36,0.5)' }}>⏸ Vár</button>
          <button type="button" disabled={selected.length === 0 || busy} onClick={() => void send({ type: 'stop' }, false)} data-testid="bulk-stop" className={BTN}
            style={{ minHeight: 44, color: '#f87171', borderColor: 'rgba(248,113,113,0.5)' }}>⏹ Leállít</button>
          <button type="button" disabled={selected.length === 0} onClick={() => open({ kind: 'groups' })} data-testid="bulk-group" className={BTN}
            style={{ minHeight: 44, color: '#a78bfa', borderColor: 'rgba(167,139,250,0.5)' }}>🏷 Csoport</button>
        </div>
        {error && !dialog && <p role="alert" className="text-xs text-red-300">{error}</p>}
      </div>

      {dialog?.kind === 'move' && (
        <ConfirmDialog title={`${selected.length} flotta mozgatása`} confirmLabel="Indulás" busy={busy}
          onCancel={() => setDialog(null)} onConfirm={() => { if (target) void send({ type: 'move', targetSystemId: target }, replace); }}>
          <div className="space-y-2" data-testid="bulk-move-dialog">
            <input type="search" aria-label="Rendszer keresése" placeholder="Rendszer neve…" value={query} onChange={e => setQuery(e.target.value)}
              className="w-full rounded-lg bg-slate-900 border border-slate-600 px-3 text-sm text-slate-200" style={{ minHeight: 44 }} />
            <ul className="space-y-1">
              {systems.map(s => (
                <li key={s.id}>
                  <button type="button" data-testid="bulk-target" data-system-id={s.id} aria-pressed={target === s.id} onClick={() => setTarget(s.id)}
                    className="w-full text-left rounded-lg border px-3 text-sm text-slate-100"
                    style={{ minHeight: 44, borderColor: target === s.id ? 'rgba(0,229,255,0.7)' : 'rgba(100,116,139,0.4)' }}>{s.name}</button>
                </li>
              ))}
            </ul>
            <label className="flex items-center gap-2 text-xs text-slate-300" style={{ minHeight: 44 }}>
              <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)} style={{ width: 20, height: 20 }} />
              A meglévő parancsok törlése, indulás azonnal
            </label>
            <p className="text-xs text-slate-500">{target ? `Cél: ${mapName(target)}. ` : 'Válassz célt. '}A szerver mindegyik flottának külön megtervezi az útvonalat; ami nem teljesíthető, azt jelzi.</p>
            {error && <p role="alert" className="text-xs text-red-300">{error}</p>}
          </div>
        </ConfirmDialog>
      )}

      {dialog?.kind === 'hold' && (
        <ConfirmDialog title={`${selected.length} flotta várakoztatása`} confirmLabel="Várakozás" busy={busy}
          onCancel={() => setDialog(null)} onConfirm={() => void send({ type: 'hold', durationSeconds: hours * 3600 }, replace)}>
          <div className="space-y-2">
            <label className="text-xs text-slate-400" htmlFor="bulk-hours">Várakozás (óra)</label>
            <select id="bulk-hours" value={hours} onChange={e => setHours(Number(e.target.value))} className="w-full rounded-lg bg-slate-900 border border-slate-600 px-3 text-sm text-slate-200" style={{ minHeight: 44 }}>
              {[1, 2, 4, 8, 12, 24].map(h => <option key={h} value={h}>{h} óra</option>)}
            </select>
            <label className="flex items-center gap-2 text-xs text-slate-300" style={{ minHeight: 44 }}>
              <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)} style={{ width: 20, height: 20 }} />
              A meglévő parancsok törlése
            </label>
            {error && <p role="alert" className="text-xs text-red-300">{error}</p>}
          </div>
        </ConfirmDialog>
      )}

      {dialog?.kind === 'groups' && (
        <GroupDialog mode="assign" groups={groups} onGroups={onGroups} fleetIds={ids} onClose={() => setDialog(null)} />
      )}

      {dialog?.kind === 'results' && (
        <ConfirmDialog title="Csoportos parancs eredménye" confirmLabel="Bezárás" onCancel={() => setDialog(null)} onConfirm={() => setDialog(null)}>
          <div data-testid="bulk-results" className="space-y-2">
            <p>{dialog.text}</p>
            <ul className="space-y-1 text-xs">
              {dialog.rows.map(r => (
                <li key={r.fleetId} className="flex justify-between gap-2" data-ok={r.ok}>
                  <span className="truncate">{r.name}</span>
                  <span className={r.ok ? 'text-emerald-300' : 'text-red-300'}>{r.ok ? 'kész' : r.message}</span>
                </li>
              ))}
            </ul>
          </div>
        </ConfirmDialog>
      )}
    </>
  );
}
